import {
  ADHDTodoManager,
  TodoItem,
  Project,
  FilterOptions,
  FocusSuggestion,
} from "./adhd-todo-manager";
import { APIClient } from "./api-client";
import { BodyDoublingSession } from "./body-doubling-service";
import { BreakType } from "./hyperfocus-break-service";
import { EnergyLevel, EnergyContext } from "./energy-context-service";

export class ADHDAPIAdapter {
  private todoManager: ADHDTodoManager;
  private apiClient: APIClient | null = null;
  private useAPI: boolean = false;

  constructor(apiClient?: APIClient) {
    this.todoManager = new ADHDTodoManager();
    if (apiClient) {
      this.apiClient = apiClient;
      this.useAPI = true;
    }
  }

  setAPIClient(apiClient: APIClient) {
    this.apiClient = apiClient;
    this.useAPI = true;
  }

  async createTodo(args: any): Promise<{ success: boolean; todo?: TodoItem; error?: string }> {
    if (this.useAPI && this.apiClient) {
      try {
        // Check for duplicates using live API data first
        const existingTodos = await this.getTodos();
        const duplicateTask = existingTodos.find(
          todo => todo.title.toLowerCase().trim() === args.title.toLowerCase().trim()
        );

        if (duplicateTask) {
          return {
            success: false,
            error: `A task with the name "${args.title}" already exists. Please choose a different name or modify the existing task.`,
          };
        }

        const response = await this.apiClient.createTodo({
          title: args.title,
          description: args.description,
          due_date: args.due_date,
          priority: args.priority,
          project_id: args.project_id,
          tags: args.tags,
          estimated_minutes: args.estimated_minutes,
          notes: args.notes,
          // ADHD-specific fields
          adhd_energy_level: args.adhd_energy_level,
          adhd_context_cost: args.adhd_context_cost,
          adhd_hyperfocus_compatible: args.adhd_hyperfocus_compatible,
        });

        if (response.success) {
          return { success: true, todo: response.data as TodoItem };
        } else {
          return { success: false, error: response.error };
        }
      } catch (error: any) {
        // Check if it's a subscription limit error - if so, fall back to localStorage
        if (error?.name === "SubscriptionLimitError") {
          console.warn("Todo creation limit reached, falling back to localStorage:", error.message);
        } else {
          console.warn("API failed, falling back to localStorage:", error);
        }
      }
    }

    return await this.todoManager.createTodo(args);
  }

  async createProject(args: any): Promise<{ success: boolean; project?: Project; error?: string }> {
    if (this.useAPI && this.apiClient) {
      try {
        // Validate and normalize the project data
        const projectData: any = {
          name: args.name,
          description: args.description,
        };

        // Handle color validation - convert to hex format or use default
        if (args.color) {
          const color = this.normalizeColor(args.color);
          if (color) {
            projectData.color = color;
          }
        }

        // Handle deadline validation - ensure it's in the future
        if (args.deadline) {
          const deadline = new Date(args.deadline);
          if (deadline > new Date()) {
            projectData.deadline = args.deadline;
          }
        }

        const response = await this.apiClient.createProject(projectData);

        if (response.success) {
          return { success: true, project: response.data as Project };
        } else {
          return { success: false, error: response.error };
        }
      } catch (error: any) {
        // Check if it's a subscription limit error - if so, fall back to localStorage
        if (error?.name === "SubscriptionLimitError") {
          console.warn(
            "Project creation limit reached, falling back to localStorage:",
            error.message
          );
        } else {
          console.warn("API failed, falling back to localStorage:", error);
        }
      }
    }

    return await this.todoManager.createProject(args);
  }

  async updateProject(args: any): Promise<{ success: boolean; project?: Project; error?: string }> {
    if (this.useAPI && this.apiClient) {
      try {
        const response = await this.apiClient.updateProject(args.project_id, {
          name: args.name,
          description: args.description,
          color: args.color,
          deadline: args.deadline,
        });

        if (response.success) {
          return { success: true, project: response.data as Project };
        } else {
          return { success: false, error: response.error };
        }
      } catch (error) {
        console.warn("API failed, falling back to localStorage:", error);
      }
    }

    return this.todoManager.updateProject(args);
  }

  async deleteProject(args: any): Promise<{ success: boolean; error?: string }> {
    if (this.useAPI && this.apiClient) {
      try {
        const response = await this.apiClient.deleteProject(args.project_id);

        if (response.success) {
          return { success: true };
        } else {
          return { success: false, error: response.error };
        }
      } catch (error) {
        console.warn("API failed, falling back to localStorage:", error);
      }
    }

    return this.todoManager.deleteProject(args);
  }

  async updateTodo(args: any): Promise<{ success: boolean; todo?: TodoItem; error?: string }> {
    if (this.useAPI && this.apiClient) {
      try {
        const response = await this.apiClient.updateTodo(args.todo_id, {
          status: args.status,
          priority: args.priority,
          due_date: args.due_date,
          estimated_minutes: args.estimated_minutes,
          notes: args.notes,
          project_id: args.project_id,
        });

        if (response.success) {
          return { success: true, todo: response.data as TodoItem };
        } else {
          return { success: false, error: response.error };
        }
      } catch (error) {
        console.warn("API failed, falling back to localStorage:", error);
      }
    }

    return this.todoManager.updateTodo(args);
  }

  async updateMultipleTodos(
    args: any
  ): Promise<{ success: boolean; updatedTodos?: TodoItem[]; error?: string }> {
    const results: TodoItem[] = [];
    const errors: string[] = [];

    for (const update of args.updates) {
      try {
        const result = await this.updateTodo({
          todo_id: update.todo_id,
          status: update.status,
          priority: update.priority,
          due_date: update.due_date,
          estimated_minutes: update.estimated_minutes,
          notes: update.notes,
        });

        if (result.success && result.todo) {
          results.push(result.todo);
        } else {
          errors.push(`Failed to update task ${update.todo_id}: ${result.error}`);
        }
      } catch (error) {
        errors.push(`Error updating task ${update.todo_id}: ${error}`);
      }
    }

    if (errors.length > 0) {
      return {
        success: false,
        error: `Some updates failed: ${errors.join(", ")}`,
        updatedTodos: results,
      };
    }

    return { success: true, updatedTodos: results };
  }

  async deleteTodo(args: any): Promise<{ success: boolean; error?: string }> {
    if (this.useAPI && this.apiClient) {
      try {
        // Support both todo_id (preferred) and task_id (for backward compatibility)
        const todoId = args.todo_id || args.task_id;

        console.log("ADHDAPIAdapter: deleteTodo called with args:", JSON.stringify(args, null, 2));
        console.log("ADHDAPIAdapter: Extracted todoId:", todoId);

        if (!todoId) {
          return { success: false, error: "No todo ID provided" };
        }

        // Validate that the todoId looks like a proper ID (not a title)
        if (todoId.length > 50 || todoId.includes(" ")) {
          console.warn("ADHDAPIAdapter: todoId appears to be a title, not an ID:", todoId);
          return {
            success: false,
            error: `Invalid todo ID format: "${todoId}". Expected a short alphanumeric ID, got what appears to be a title. Please provide the todo_id field, not the title.`,
          };
        }

        const response = await this.apiClient.deleteTodo(todoId);

        if (response.success) {
          return { success: true };
        } else {
          return { success: false, error: response.error };
        }
      } catch (error) {
        console.warn("API failed, falling back to localStorage:", error);
      }
    }

    return this.todoManager.deleteTodo(args);
  }

  async getFilteredTodos(args: FilterOptions): Promise<TodoItem[]> {
    if (this.useAPI && this.apiClient) {
      try {
        const filters: any = {
          filter_type: args.filter_type || "all",
        };

        // Pass through filter-specific parameters
        if (args.project_id) {
          filters.project_id = args.project_id;
        }
        if (args.available_time_minutes) {
          filters.available_time_minutes = args.available_time_minutes;
        }

        const response = await this.apiClient.getTodos(filters);

        if (response.success) {
          return response.data as TodoItem[];
        }
      } catch (error) {
        console.warn("API failed, falling back to localStorage:", error);
      }
    }

    return this.todoManager.getFilteredTodos(args);
  }

  async getFocusSuggestions(args?: {
    available_time_minutes?: number;
    current_energy_level?: EnergyLevel;
    current_context?: string;
    prefer_hyperfocus?: boolean;
  }): Promise<FocusSuggestion[]> {
    if (this.useAPI && this.apiClient) {
      try {
        // Use our enhanced ADHD focus recommendations with live API data
        return await this.getADHDFocusRecommendations({
          availableTimeMinutes: args?.available_time_minutes,
          currentEnergyLevel: args?.current_energy_level,
          preferHyperfocus: args?.prefer_hyperfocus,
        });
      } catch (error) {
        console.error("API focus suggestions failed:", error);
        return [];
      }
    }

    return this.todoManager.getFocusSuggestions(args || {});
  }

  async startBodyDoubling(args: {
    task_id: string;
    duration_minutes?: number;
    check_in_frequency?: number;
  }): Promise<{ success: boolean; session?: BodyDoublingSession; error?: string }> {
    // Body doubling is handled locally
    return this.todoManager.startBodyDoubling(args);
  }

  async setHyperfocusBreak(args: {
    break_interval?: number;
    break_type?: BreakType;
  }): Promise<{ success: boolean; message: string; error?: string }> {
    // Hyperfocus breaks are handled locally
    return this.todoManager.setHyperfocusBreak(args);
  }

  async setEnergyContext(args: {
    energy_level: EnergyLevel;
    context_description?: string;
    available_time_minutes?: number;
  }): Promise<{ success: boolean; context: EnergyContext; message: string; error?: string }> {
    // Energy context is handled locally
    return this.todoManager.setEnergyContext(args);
  }

  async getTodos(): Promise<TodoItem[]> {
    console.log(
      "ADHDAPIAdapter: getTodos called, useAPI:",
      this.useAPI,
      "hasClient:",
      !!this.apiClient
    );

    if (this.useAPI && this.apiClient) {
      try {
        console.log("ADHDAPIAdapter: Making API call to getTodos");
        const response = await this.apiClient.getTodos({ filter_type: "all" });
        console.log("ADHDAPIAdapter: API response:", response);
        if (response.success) {
          return response.data as TodoItem[];
        }
      } catch (error) {
        console.warn("API failed, falling back to localStorage:", error);
      }
    }

    console.log("ADHDAPIAdapter: Using localStorage fallback");
    return this.todoManager.getTodos();
  }

  async getProjects(): Promise<Project[]> {
    console.log(
      "ADHDAPIAdapter: getProjects called, useAPI:",
      this.useAPI,
      "hasClient:",
      !!this.apiClient
    );

    if (this.useAPI && this.apiClient) {
      try {
        console.log("ADHDAPIAdapter: Making API call to getProjects");
        const response = await this.apiClient.getProjects();
        console.log("ADHDAPIAdapter: API response:", response);
        if (response.success) {
          return response.data as Project[];
        }
      } catch (error) {
        console.warn("API failed, falling back to localStorage:", error);
      }
    }

    console.log("ADHDAPIAdapter: Using localStorage fallback");
    return this.todoManager.getProjects();
  }

  async migrateLocalDataToAPI(): Promise<{ success: boolean; error?: string }> {
    if (!this.useAPI || !this.apiClient) {
      return { success: false, error: "API client not available" };
    }

    try {
      const todos = this.todoManager.getTodos();
      const projects = this.todoManager.getProjects();

      for (const project of projects) {
        const projectData: any = {
          name: project.name,
          description: project.description,
        };

        // Handle color validation
        if (project.color) {
          const color = this.normalizeColor(project.color);
          if (color) {
            projectData.color = color;
          }
        }

        // Handle deadline validation
        if (project.deadline) {
          const deadline = new Date(project.deadline);
          if (deadline > new Date()) {
            projectData.deadline = project.deadline;
          }
        }

        await this.apiClient.createProject(projectData);
      }

      for (const todo of todos) {
        await this.apiClient.createTodo({
          title: todo.title,
          description: todo.description,
          due_date: todo.due_date,
          priority: todo.priority === "urgent" ? "high" : todo.priority,
          project_id: todo.project_id,
          tags: todo.tags,
        });
      }

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Migration failed",
      };
    }
  }

  // New automatic prioritization methods
  async reprioritizeAllTodos(): Promise<{
    success: boolean;
    updatedCount: number;
    error?: string;
  }> {
    if (this.useAPI && this.apiClient) {
      try {
        // Get current data from API
        const todos = await this.getTodos();
        console.log("Reprioritizing", todos.length, "todos from API");

        // Use todoManager to analyze priorities but work with API data
        // We'll need to implement API-based reprioritization
        // For now, fall back to localStorage approach
        console.warn("API-based reprioritization not yet implemented, using localStorage fallback");
        return this.todoManager.reprioritizeAllTodos();
      } catch (error) {
        console.error("API reprioritization failed:", error);
        return {
          success: false,
          updatedCount: 0,
          error: error instanceof Error ? error.message : "Reprioritization failed",
        };
      }
    }

    return this.todoManager.reprioritizeAllTodos();
  }

  async getTasksByQuadrant(
    quadrant: 1 | 2 | 3 | 4,
    includeADHDContext?: boolean
  ): Promise<TodoItem[]> {
    if (this.useAPI && this.apiClient) {
      try {
        const todos = await this.getTodos();
        // Filter by quadrant using live API data
        const filtered = todos.filter(
          t => t.status !== "completed" && t.eisenhower_quadrant === quadrant
        );

        if (includeADHDContext) {
          // Sort by ADHD-friendly factors using live data
          return filtered.sort((a, b) => {
            const confidenceA = a.priority_confidence || 0;
            const confidenceB = b.priority_confidence || 0;
            if (confidenceA !== confidenceB) {
              return confidenceB - confidenceA;
            }
            const contextCostA = a.adhd_context_cost || 5;
            const contextCostB = b.adhd_context_cost || 5;
            return contextCostA - contextCostB;
          });
        }

        return filtered;
      } catch (error) {
        console.error("API quadrant query failed:", error);
        return [];
      }
    }

    return this.todoManager.getTasksByQuadrant(quadrant, includeADHDContext);
  }

  async getADHDFocusRecommendations(context: {
    availableTimeMinutes?: number;
    currentEnergyLevel?: "low" | "medium" | "high";
    preferHyperfocus?: boolean;
  }): Promise<FocusSuggestion[]> {
    if (this.useAPI && this.apiClient) {
      try {
        const todos = await this.getTodos();
        const projects = await this.getProjects();

        // Use todoManager analysis but with live API data
        // Create a temporary manager with live data for analysis
        const tempManager = new (await import("./adhd-todo-manager")).ADHDTodoManager();
        tempManager.saveTodos(todos);
        tempManager.saveProjects(projects);

        return tempManager.getADHDFocusRecommendations(context);
      } catch (error) {
        console.error("API focus recommendations failed:", error);
        return [];
      }
    }

    return this.todoManager.getADHDFocusRecommendations(context);
  }

  async getPrioritizationInsights(): Promise<{
    lowConfidenceCount: number;
    avgConfidence: number;
    quadrantDistribution: { [key: number]: number };
    suggestions: string[];
  }> {
    if (this.useAPI && this.apiClient) {
      try {
        const todos = await this.getTodos();

        // Analyze live API data
        const lowConfidenceThreshold = 60;
        let lowConfidenceCount = 0;
        let totalConfidence = 0;
        const quadrantDistribution: { [key: number]: number } = { 1: 0, 2: 0, 3: 0, 4: 0 };
        const suggestions: string[] = [];

        const activeTodos = todos.filter(t => t.status !== "completed");

        activeTodos.forEach(todo => {
          const confidence = todo.priority_confidence || 0;
          totalConfidence += confidence;

          if (confidence < lowConfidenceThreshold) {
            lowConfidenceCount++;
          }

          if (todo.eisenhower_quadrant) {
            quadrantDistribution[todo.eisenhower_quadrant]++;
          }
        });

        const avgConfidence = activeTodos.length > 0 ? totalConfidence / activeTodos.length : 0;

        // Generate suggestions based on live data
        if (lowConfidenceCount > 0) {
          suggestions.push(
            `${lowConfidenceCount} tasks have low prioritization confidence. Consider adding more context or due dates.`
          );
        }

        if (quadrantDistribution[1] > 5) {
          suggestions.push(
            `You have ${quadrantDistribution[1]} urgent tasks. Consider delegating or eliminating some to prevent ADHD overwhelm.`
          );
        }

        if (quadrantDistribution[4] > quadrantDistribution[2]) {
          suggestions.push(
            "You have more low-priority tasks than scheduled important tasks. Consider reviewing your task list for elimination opportunities."
          );
        }

        return {
          lowConfidenceCount,
          avgConfidence: Math.round(avgConfidence),
          quadrantDistribution,
          suggestions,
        };
      } catch (error) {
        console.error("API prioritization insights failed:", error);
        return {
          lowConfidenceCount: 0,
          avgConfidence: 0,
          quadrantDistribution: { 1: 0, 2: 0, 3: 0, 4: 0 },
          suggestions: ["Unable to analyze priorities - API error"],
        };
      }
    }

    return this.todoManager.getPrioritizationInsights();
  }

  // New Task-Project Management Methods
  async findTaskByName(args: {
    task_name: string;
    exact_match?: boolean;
  }): Promise<{ success: boolean; tasks?: TodoItem[]; error?: string }> {
    try {
      const todos = await this.getTodos(); // Use live API data
      const searchTerm = args.task_name.toLowerCase().trim();

      if (args.exact_match) {
        const exactMatches = todos.filter(todo => todo.title.toLowerCase().trim() === searchTerm);
        return { success: true, tasks: exactMatches };
      } else {
        const partialMatches = todos.filter(todo => todo.title.toLowerCase().includes(searchTerm));
        return { success: true, tasks: partialMatches };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async findProjectByName(args: {
    project_name: string;
    exact_match?: boolean;
  }): Promise<{ success: boolean; projects?: Project[]; error?: string }> {
    try {
      const projects = await this.getProjects(); // Use live API data
      const searchTerm = args.project_name.toLowerCase().trim();

      if (args.exact_match) {
        const exactMatches = projects.filter(
          project => project.name.toLowerCase().trim() === searchTerm
        );
        return { success: true, projects: exactMatches };
      } else {
        const partialMatches = projects.filter(project =>
          project.name.toLowerCase().includes(searchTerm)
        );
        return { success: true, projects: partialMatches };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async moveTaskToProject(args: {
    task_identifier: string;
    project_identifier?: string;
  }): Promise<{
    success: boolean;
    task?: TodoItem;
    project?: Project;
    message?: string;
    error?: string;
  }> {
    console.log("ADHDAPIAdapter.moveTaskToProject called with:", args);

    if (this.useAPI && this.apiClient) {
      try {
        // Get current data from API
        const todos = await this.getTodos();
        const projects = await this.getProjects();

        console.log("API data - todos:", todos.length, "projects:", projects.length);

        // Find the task
        let targetTask: TodoItem | undefined;

        // First try to find by ID
        targetTask = todos.find(t => t.id === args.task_identifier);
        console.log("Task found by ID:", !!targetTask);

        // If not found, search by title
        if (!targetTask) {
          console.log("Searching for task by name:", args.task_identifier);
          const searchTerm = args.task_identifier.toLowerCase().trim();
          const partialMatches = todos.filter(todo =>
            todo.title.toLowerCase().includes(searchTerm)
          );

          if (partialMatches.length > 0) {
            targetTask = partialMatches[0];
            console.log("Using first match:", targetTask.title);
          }
        }

        if (!targetTask) {
          console.log("Task not found:", args.task_identifier);
          return {
            success: false,
            error: `Task "${args.task_identifier}" not found. Please check the task name or try being more specific.`,
          };
        }

        // Handle project assignment
        let targetProjectId: string | undefined;
        let targetProject: Project | undefined;

        if (
          !args.project_identifier ||
          args.project_identifier.toLowerCase() === "none" ||
          args.project_identifier.toLowerCase() === "unassigned"
        ) {
          // Unassign from project
          targetProjectId = undefined;
        } else {
          // Find the project
          targetProject = projects.find(p => p.id === args.project_identifier);

          // If not found, search by name
          if (!targetProject) {
            console.log("Searching for project by name:", args.project_identifier);
            const searchTerm = args.project_identifier.toLowerCase().trim();
            const partialMatches = projects.filter(project =>
              project.name.toLowerCase().includes(searchTerm)
            );

            if (partialMatches.length > 0) {
              targetProject = partialMatches[0];
              console.log("Using first project match:", targetProject.name);
            }
          }

          if (!targetProject) {
            console.log("Project not found:", args.project_identifier);
            return {
              success: false,
              error: `Project "${args.project_identifier}" not found. Please check the project name or create the project first.`,
            };
          }

          targetProjectId = targetProject.id;
        }

        // Update the task via API
        const apiResponse = await this.apiClient.updateTodo(targetTask.id, {
          project_id: targetProjectId,
        });

        if (!apiResponse.success) {
          return {
            success: false,
            error: `Failed to update task: ${apiResponse.error}`,
          };
        }

        // Generate success message
        let message: string;
        if (targetProject) {
          message = `Task "${targetTask.title}" has been moved to project "${targetProject.name}".`;
        } else {
          message = `Task "${targetTask.title}" has been unassigned from all projects.`;
        }

        return {
          success: true,
          task: apiResponse.data as TodoItem,
          project: targetProject,
          message,
        };
      } catch (error) {
        console.error("API-based task movement failed:", error);
        return {
          success: false,
          error: error instanceof Error ? error.message : "Failed to move task",
        };
      }
    }

    // Fallback to local implementation
    console.log("Using localStorage fallback for task movement");
    return this.todoManager.moveTaskToProject(args);
  }

  async batchMoveTasksToProject(args: {
    task_identifiers: string[];
    project_identifier: string;
  }): Promise<{
    success: boolean;
    moved?: Array<{ task: TodoItem; project?: Project }>;
    failed?: Array<{ identifier: string; error: string }>;
    message?: string;
    error?: string;
  }> {
    if (this.useAPI && this.apiClient) {
      // Use API-based implementation for consistency
      const moved: Array<{ task: TodoItem; project?: Project }> = [];
      const failed: Array<{ identifier: string; error: string }> = [];

      for (const taskIdentifier of args.task_identifiers) {
        try {
          const result = await this.moveTaskToProject({
            task_identifier: taskIdentifier,
            project_identifier: args.project_identifier,
          });

          if (result.success && result.task) {
            moved.push({
              task: result.task,
              project: result.project,
            });
          } else {
            failed.push({
              identifier: taskIdentifier,
              error: result.error || "Unknown error",
            });
          }
        } catch (error) {
          failed.push({
            identifier: taskIdentifier,
            error: error instanceof Error ? error.message : "Unknown error",
          });
        }
      }

      const totalTasks = args.task_identifiers.length;
      const successCount = moved.length;
      const failureCount = failed.length;

      let message: string;
      if (successCount === totalTasks) {
        const projectName = moved[0]?.project?.name || "unassigned";
        message = `Successfully moved all ${successCount} tasks to ${projectName === "unassigned" ? "unassigned tasks" : `project "${projectName}"`}.`;
      } else if (successCount > 0) {
        message = `Moved ${successCount} out of ${totalTasks} tasks. ${failureCount} tasks could not be moved.`;
      } else {
        message = `Failed to move any tasks. Please check the task and project names.`;
      }

      return {
        success: successCount > 0,
        moved: moved.length > 0 ? moved : undefined,
        failed: failed.length > 0 ? failed : undefined,
        message,
      };
    }

    // Fallback to localStorage implementation
    return this.todoManager.batchMoveTasksToProject(args);
  }

  private normalizeColor(color: string): string | null {
    // If already hex format, validate and return
    if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
      return color;
    }

    // Color name to hex mapping for common colors
    const colorMap: Record<string, string> = {
      red: "#ff4444",
      blue: "#4444ff",
      green: "#44ff44",
      yellow: "#ffff44",
      orange: "#ff8844",
      purple: "#8844ff",
      pink: "#ff44ff",
      cyan: "#44ffff",
      magenta: "#ff44ff",
      lime: "#88ff44",
      indigo: "#4400ff",
      teal: "#008080",
      navy: "#000080",
      maroon: "#800000",
      olive: "#808000",
      gray: "#808080",
      grey: "#808080",
      black: "#000000",
      white: "#ffffff",
      silver: "#c0c0c0",
      gold: "#ffd700",
    };

    const lowerColor = color.toLowerCase().trim();
    return colorMap[lowerColor] || null;
  }
}
